import React, { useMemo } from 'react';
import { Card, Typography, Box, Stack, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import thunderbolt from '@iconify/icons-ant-design/thunderbolt';
import { BaseChart } from 'src/components/charts/BaseOptionChart';
import { isSameDay } from 'date-fns';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.warning.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.warning.lighter}08 0%, ${theme.palette.warning.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
  },
}));

const MetricCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.warning.lighter}15 50%, ${theme.palette.warning.light}08 100%)`,
  border: `1px solid ${theme.palette.warning.light}30`,
  textAlign: 'center',
  height: '100%',
  minHeight: '120px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.warning.light}20`,
    border: `1px solid ${theme.palette.warning.light}50`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.light} 100%)`,
    borderRadius: `${theme.shape.borderRadius * 1.5}px ${theme.shape.borderRadius * 1.5}px 0 0`,
  },
}));

// Process energy consumption data from transactions
const processEnergyConsumptionData = (apiData, timeInterval) => {
  if (!apiData || !Array.isArray(apiData)) {
    return { data: [], labels: [] };
  }

  const { start, end } = timeInterval;
  const startDate = new Date(start);
  const endDate = new Date(end);
  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  
  // Initialize arrays for each day
  const totalEnergyConsumed = new Array(days).fill(0);
  const successfulSessions = new Array(days).fill(0);
  const labels = [];

  // Generate labels with dates for comparison
  const labelDates = [];
  for (let i = 0; i < days; i++) {
    const currentDate = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    labelDates.push(currentDate);
    labels.push(currentDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    }));
  }

  // Process transactions
  apiData.forEach((txn) => {
    const rawTime = txn.start_time || txn.created_at;
    if (!rawTime) return;

    // Robust parsing: Ensure we have an ISO-like string and handle the UTC shift.
    // 1. Remove AM/PM if present (Standard browser Date parser handles HH:MM:SS better)
    // 2. Trim microseconds (more than 3 digits after dot)
    // 3. Append 'Z' to treat as UTC (aligning with backend storage and other charts)
    let timeStr = String(rawTime).replace(' ', 'T');
    
    // Remove space before AM/PM if it exists, but usually MySQL doesn't include it. 
    // If it does, we strip it for reliability.
    timeStr = timeStr.replace(/\s*[AP]M$/i, ''); 
    
    // Trim fractional seconds to 3 digits
    timeStr = timeStr.replace(/(\.\d{3})\d+/, '$1');
    
    // Force UTC if no timezone info is present
    const finalTimeStr = timeStr.includes('Z') || timeStr.includes('+') ? timeStr : timeStr + 'Z';
    const txnDate = new Date(finalTimeStr);
    
    // Check if transaction is within our date range
    if (!isNaN(txnDate.getTime()) && txnDate >= startDate && txnDate <= endDate) {
      const dayIndex = Math.floor((txnDate - startDate) / (24 * 60 * 60 * 1000));
      
      if (dayIndex >= 0 && dayIndex < days) {
        const energyConsumed = parseFloat(txn.energy_consumed || 0);
        const amountCharged = parseFloat(txn.totalAmountCharged || 0);
        
        // Match the "general rule": if it started in the period, count it.
        // We prioritize energy consumed for this chart.
        if (energyConsumed > 0) {
          totalEnergyConsumed[dayIndex] += energyConsumed;
          
          // Only increment session count for successful sessions (consistent with other metrics)
          if (amountCharged >= 0) {
            successfulSessions[dayIndex]++;
          }
        }
      }
    }
  });

  // Remove only the last date if it's in the future (not today)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let filteredLabels = labels;
  let filteredTotalEnergyConsumed = totalEnergyConsumed;
  let filteredSuccessfulSessions = successfulSessions;

  // Check only the last date
  if (labelDates.length > 0) {
    const lastDate = new Date(labelDates[labelDates.length - 1]);
    lastDate.setHours(0, 0, 0, 0);
    
    // Remove last date only if it's in the future (strictly greater than today)
    if (lastDate > today) {
      filteredLabels = labels.slice(0, -1);
      filteredTotalEnergyConsumed = totalEnergyConsumed.slice(0, -1);
      filteredSuccessfulSessions = successfulSessions.slice(0, -1);
    }
  }

  return {
    data: {
      totalEnergyConsumed: filteredTotalEnergyConsumed,
      successfulSessions: filteredSuccessfulSessions,
      totalSessions: filteredSuccessfulSessions, // Add this to prevent reduce error on undefined
      labels: filteredLabels
    }
  };
};

// Chart options for energy consumption
const getChartOptions = (labels, yAxisTitle = 'Energy (kWh)') => ({
  chart: {
    type: 'area',
    toolbar: { show: false },
    zoom: { enabled: false },
    pan: { enabled: false },
    selection: { enabled: false },
    brush: { enabled: false },
    animations: { enabled: true },
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    padding: {
      top: 60,
      right: 20,
      bottom: 20,
      left: 20
    }
  },
  colors: ['#3182CE', '#38A169'],
  title: {
    align: 'left',
    style: {
      fontSize: '12px',
      fontWeight: 400,
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: '#718096'
    }
  },
  stroke: {
    curve: 'smooth',
    width: 3,
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.3,
      opacityTo: 0.1,
      stops: [0, 100]
    }
  },
  markers: {
    size: 4,
    strokeWidth: 2,
    hover: {
      size: 6,
    },
  },
  xaxis: {
    categories: labels,
    title: {
      text: 'Date',
      align: 'left',
      style: {
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#718096'
      },
    },
    labels: {
      align: 'left',
      style: {
        fontSize: '11px',
        fontWeight: 400,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        colors: '#4A5568'
      }
    }
  },
  yaxis: {
    title: {
      text: yAxisTitle,
      align: 'left',
      style: {
        fontSize: '12px',
        fontWeight: 500,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#718096'
      },
    },
    labels: {
      align: 'left',
      style: {
        fontSize: '11px',
        fontWeight: 400,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        colors: '#4A5568'
      },
      formatter: (value) => `${value.toFixed(1)}`
    }
  },
  grid: {
    show: true,
    borderColor: '#E2E8F0',
    strokeDashArray: 3,
    xaxis: {
      lines: {
        show: false
      }
    },
    yaxis: {
      lines: {
        show: true
      }
    }
  },
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'left',
    fontSize: '12px',
    fontWeight: 500,
    markers: {
      radius: 5,
    },
    itemMargin: {
      horizontal: 12,
      vertical: 8,
    },
    labels: {
      colors: '#4A5568'
    },
    offsetY: 0,
    floating: false,
    width: '100%',
    height: 'auto',
    formatter: function(seriesName, opts) {
      return seriesName;
    },
    onItemClick: {
      toggleDataSeries: true
    },
    onItemHover: {
      highlightDataSeries: true
    }
  },
  tooltip: {
    style: {
      fontSize: '12px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    y: {
      formatter: (value) => `${value.toFixed(1)} kWh`
    },
    followCursor: true,
    offsetY: -10,
    offsetX: 0,
    fixed: {
      enabled: false
    },
    position: 'top',
    custom: function({ series, seriesIndex, dataPointIndex, w }) {
      const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
      const value = data ? data.toFixed(1) : '0.0';
      return `
        <div style="
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          max-width: 200px;
          word-wrap: break-word;
          z-index: 1000;
        ">
          <div style="font-weight: 600; margin-bottom: 4px;">Energy Consumption</div>
          <div>${value} kWh</div>
        </div>
      `;
    }
  },
  dataLabels: {
    enabled: false
  }
});

const EnergyConsumption = ({ 
  timeInterval, 
  transactionsData, 
  isLoading = false, 
  error = null
}) => {
  // Process energy consumption data
  const energyData = useMemo(() => {
    if (!transactionsData) {
      return null;
    }
    
    // Convert object to array if needed
    let transactionsArray;
    
    if (Array.isArray(transactionsData)) {
      transactionsArray = transactionsData;
    } else if (transactionsData && typeof transactionsData === 'object') {
      transactionsArray = Object.values(transactionsData);
    } else {
      return null;
    }
    
    if (!Array.isArray(transactionsArray) || transactionsArray.length === 0) {
      return null;
    }

    return processEnergyConsumptionData(transactionsArray, timeInterval);
  }, [transactionsData, timeInterval]);

  const chartData = useMemo(() => {
    if (!energyData || !energyData.data) {
      // Return empty data when no transactions
      const days = 7; // Default to 7 days
      const emptyData = new Array(days).fill(0);
      const labels = [];
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const labelDatesForEmpty = [];
      for (let i = 0; i < days; i++) {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() - (days - 1 - i));
        currentDate.setHours(0, 0, 0, 0);
        labelDatesForEmpty.push(currentDate);
        labels.push(currentDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }));
      }
      
      // Remove only the last date if it's in the future (not today)
      if (labelDatesForEmpty.length > 0) {
        const lastDate = new Date(labelDatesForEmpty[labelDatesForEmpty.length - 1]);
        lastDate.setHours(0, 0, 0, 0);
        if (lastDate > today) {
          labels.pop();
        }
      }
      
      // Adjust emptyData array length to match labels
      const adjustedEmptyData = new Array(labels.length).fill(0);
      
      return {
        series: [
          {
            name: 'Energy Consumed',
            data: adjustedEmptyData,
            color: '#3182CE',
          },
          {
            name: 'Successful Sessions',
            data: adjustedEmptyData,
            color: '#38A169',
          },
        ],
        options: getChartOptions(labels, 'Energy (kWh)')
      };
    }

    const { totalEnergyConsumed, successfulSessions, labels } = energyData.data;
    
    const datasets = [
      {
        name: 'Energy Consumed',
        data: totalEnergyConsumed,
        color: '#3182CE',
      },
      {
        name: 'Successful Sessions',
        data: successfulSessions,
        color: '#38A169',
      },
    ];

    return {
      series: datasets,
      options: getChartOptions(labels, 'Energy (kWh)')
    };
  }, [energyData]);

  // Calculate metrics
  const totalEnergyConsumed = energyData?.data?.totalEnergyConsumed?.reduce((sum, val) => sum + val, 0) || 0;
  const totalSuccessfulSessions = energyData?.data?.successfulSessions?.reduce((sum, val) => sum + val, 0) || 0;
  const totalSessions = energyData?.data?.totalSessions?.reduce((sum, val) => sum + val, 0) || 0;
  const totalFailedSessions = totalSessions - totalSuccessfulSessions;
  const avgDailyEnergy = Math.round((totalEnergyConsumed / (energyData?.data?.totalEnergyConsumed?.length || 1)) * 10) / 10;
  const avgEnergyPerSession = totalSuccessfulSessions > 0 ? Math.round((totalEnergyConsumed / totalSuccessfulSessions) * 10) / 10 : 0;

  // Loading state
  if (isLoading) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Loading energy consumption data...
            </Typography>
          </Stack>
        </Box>
      </StyledCard>
    );
  }

  // Error state
  if (error) {
    return (
      <StyledCard>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Failed to load energy consumption data. Please try again.
          </Alert>
        </Box>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          Energy Consumption
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Track energy usage and charging efficiency
        </Typography>

        {/* Key Metrics - 2 Rows */}
        <Box sx={{ mb: 1 }}>
          {/* First Row */}
          <Stack direction="row" spacing={2} sx={{ mb: 1, alignItems: 'stretch' }}>
            <MetricCard sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {totalEnergyConsumed.toFixed(1)} kWh
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                Total Energy
              </Typography>
            </MetricCard>

            <MetricCard sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {avgDailyEnergy.toFixed(1)} kWh
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                Avg Daily
              </Typography>
            </MetricCard>
          </Stack>

          {/* Second Row */}
          {/* <Stack direction="row" spacing={2} sx={{ alignItems: 'stretch' }}>
            <MetricCard sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {avgEnergyPerSession.toFixed(1)} kWh
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                Avg per Session
              </Typography>
            </MetricCard>
          </Stack> */}
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ 
        flex: 1, 
        p: { xs: 1, sm: 2 }, 
        pb: { xs: 2, sm: 3 },
        overflow: 'hidden',
        width: '100%'
      }}>
        <Box sx={{ 
          width: '100%', 
          height: { xs: 250, sm: 300, md: 350 },
          overflow: 'hidden',
          position: 'relative'
        }}>
          <BaseChart
            key={`energy-consumption-chart-${transactionsData?.length || 0}-${timeInterval?.type || 'default'}`}
            series={chartData.series}
            options={chartData.options}
            type="area"
            height="100%"
            width="100%"
          />
        </Box>
      </Box>
    </StyledCard>
  );
};

export default EnergyConsumption;